import {
  TextField,
  MenuItem,
  Avatar,
  SxProps,
  Typography,
} from '@mui/material';
import { useIsDesktop } from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import { ReactNode } from 'react';
import {
  FIELD_CLEAR_ICON_GAP,
  FIELD_HEIGHT,
  FIELD_ICON_INSET,
  FIELD_ICON_MOBILE_SX,
  FIELD_ICON_SX,
  FIELD_MENU_SX,
  FIELD_RADIUS,
} from '../FieldTokens';

interface IControlledSelectInput2 {
  options: string[];
  value: string;
  width?: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  ) => void;
  ariaLabelledById?: string;
  ariaLabel?: string;
  ariaRequired?: boolean;
  label?: string;
  placeholder?: string;
  sx?: SxProps;
  divWrapperSx?: any;
  size: 'small' | 'medium';
  id?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  iconSrc?: string;
  widthOffset?: string;
  disablePortal?: boolean;
  disabled?: boolean;
  onOpen?: () => void;
}

export const ControlledSelectInput2 = ({
  onChange,
  options,
  value,
  width,
  onFocus,
  onBlur,
  ariaLabelledById,
  ariaLabel,
  ariaRequired,
  label,
  placeholder,
  sx,
  divWrapperSx,
  endAdornment,
  iconSrc = AccordionArrowPurple,
  size,
  id,
  startAdornment,
  widthOffset,
  disablePortal,
  disabled,
  onOpen,
}: IControlledSelectInput2) => {
  const isDesktop = useIsDesktop();
  const clearIconSize = isDesktop
    ? FIELD_ICON_SX.width
    : FIELD_ICON_MOBILE_SX.width;
  const selectClearPadding = `calc(${FIELD_ICON_INSET} + ${clearIconSize} + ${FIELD_CLEAR_ICON_GAP}) !important`;
  return (
    <TextField
      disabled={disabled}
      id={id?.trim().replace(' ', '-')}
      value={value}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={onChange}
      label={label}
      aria-labelledby={ariaLabelledById ? ariaLabelledById : undefined}
      select
      fullWidth={!width}
      size={size}
      sx={{
        width: width,
        borderRadius: FIELD_RADIUS,
        bgcolor: '#fff',

        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: FIELD_RADIUS,
          borderColor: 'primary.shade_200',
          borderWidth: '1px',
          // MUI lifts the outline 5px to make room for the floating label notch.
          // With no label the border would just overshoot the field.
          top: label ? undefined : 0,
          '& legend': {
            textAlign: 'right',
            direction: 'rtl',
            '& span': {
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        '.MuiInputLabel-root': {
          fontWeight: 400,
          right: '12px',
          left: 'auto',
          transformOrigin: 'top right',
        },
        '&&& .MuiInputLabel-root.MuiInputLabel-shrink': {
          right: 0,
          transform: 'translate(-14px, -9px) scale(0.75)',
        },
        '.MuiOutlinedInput-root': {
          height: FIELD_HEIGHT,
          fontWeight: 400,
          ...(startAdornment
            ? {
                pl: 0,
                paddingInlineStart: FIELD_ICON_INSET,
              }
            : {}),

          '&:hover fieldset': {
            borderColor: 'primary.main',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
            borderWidth: '1px',
          },
          '& .MuiSelect-select': {
            pl: '48px',
            pr: startAdornment
              ? '0 !important'
              : endAdornment
                ? selectClearPadding
                : '14px !important',
          },
        },
        '& .MuiSelect-icon': {
          cursor: 'pointer',
          top: 'auto',
          right: 'auto',
          left: FIELD_ICON_INSET,
        },
        '& .MuiInputAdornment-positionEnd': {
          position: 'absolute',
          right: FIELD_ICON_INSET,
        },
        '.MuiSelect-iconOpen': {
          top: 'auto',
          right: 'auto',
          left: FIELD_ICON_INSET,
        },
        '&.MuiFormControl-root': {
          height: FIELD_HEIGHT,
          '& div': {
            ...divWrapperSx,
          },
        },
        '& .MuiFormLabel-root[data-shrink="false"]': {
          pr: '14px',
        },
        ...sx,
      }}
      slotProps={{
        input: {
          startAdornment,
          endAdornment,
        },

        htmlInput: {
          'aria-label': ariaLabel,
          'aria-required': ariaRequired,
        },

        select: {
          onOpen,
          displayEmpty: !!placeholder,
          // Reads the controlled `value` instead of the callback argument so the
          // empty state stays typed as a string rather than `unknown`.
          renderValue: placeholder
            ? () =>
                value || (
                  <Typography
                    component='span'
                    variant='inherit'
                    sx={{ color: 'text.muted' }}
                  >
                    {placeholder}
                  </Typography>
                )
            : undefined,
          // Without a label MUI points aria-labelledby at the combobox itself,
          // which shadows aria-label and leaves the field with no accessible name.
          SelectDisplayProps: label
            ? undefined
            : { 'aria-label': ariaLabel, 'aria-labelledby': undefined },
          MenuProps: {
            disablePortal: disablePortal,
            PaperProps: {
              sx: {
                ...FIELD_MENU_SX,
                width: width,
                marginRight: widthOffset,
                padding: 0,
                color: 'text.secondary',
                boxShadow: '0px 6px 12px 0px rgba(21, 82, 232, 0.06)',
                maxHeight: '200px',
                '& ul': {
                  p: 0,
                },
              },
            },
          },
          IconComponent: (props) => (
            <Avatar
              {...props}
              src={iconSrc}
              alt={''}
              sx={{ width: '24px', height: '24px' }}
            />
          ),
        },
      }}
    >
      {options.map((value, index) => (
        <MenuItem
          key={index}
          value={value}
          sx={{
            fontSize: '18px !important',
            fontWeight: '400 !important',
            padding: '14px 20px 14px',
            '&:hover': {
              fontWeight: '700 !important',
              bgcolor: 'primary.shade_100',
            },
            '&.Mui-selected': {
              fontWeight: '700 !important',
              bgcolor: 'primary.shade_100',
            },
          }}
        >
          {value}
        </MenuItem>
      ))}
    </TextField>
  );
};
